import { useMemo } from 'react'
import { graphql } from 'gatsby'
import tw, { css } from 'twin.macro'
import { Global } from '@emotion/react'

import hljs from 'highlight.js/lib/core'

import bash from 'highlight.js/lib/languages/bash'
import csharp from 'highlight.js/lib/languages/csharp'
import cpp from 'highlight.js/lib/languages/cpp'
import go from 'highlight.js/lib/languages/go'
import java from 'highlight.js/lib/languages/java'
import javascript from 'highlight.js/lib/languages/javascript'
import plaintext from 'highlight.js/lib/languages/plaintext'
import php from 'highlight.js/lib/languages/php'
import python from 'highlight.js/lib/languages/python'
import ruby from 'highlight.js/lib/languages/ruby'
import rust from 'highlight.js/lib/languages/rust'
import xml from 'highlight.js/lib/languages/xml'
import yaml from 'highlight.js/lib/languages/yaml'
import { isPrint } from '@/utils/print'

import CopyButton from '../copy-button'

export interface Props {
  body: {
    body: string
  }
  language?: string
  wordWrap?: boolean
}

export const fragment = graphql`
  fragment Code on ContentfulComponentCode {
    __typename
    contentful_id
    body {
      body
    }
    language
    wordWrap
  }
`

export default function Code({ body, language: langStr = 'Bash', wordWrap }: Props): JSX.Element {
  const isPrintView = isPrint()
  const highlightedCode = useMemo(() => {
    const languageMap = {
      Bash: {
        key: 'bash',
        file: bash,
        extraKeywords: ['bw', 'bws', 'alias', 'chmod', 'echo', 'export', 'npm', 'yarn'],
      },
      'C++': {
        key: 'cpp',
        file: cpp,
      },
      'C#': {
        key: 'csharp',
        file: csharp,
      },
      Go: {
        key: 'go',
        file: go,
      },
      HTML: {
        key: 'xml',
        file: xml,
      },
      Java: {
        key: 'java',
        file: java,
      },
      JavaScript: {
        key: 'javascript',
        file: javascript,
      },
      'Plain Text': {
        key: 'plaintext',
        file: plaintext,
      },
      PHP: {
        key: 'php',
        file: php,
      },
      Python: {
        key: 'python',
        file: python,
      },
      Ruby: {
        key: 'ruby',
        file: ruby,
      },
      Rust: {
        key: 'rust',
        file: rust,
      },
      XML: {
        key: 'xml',
        file: xml,
      },
      YAML: {
        key: 'yaml',
        file: yaml,
      },
    }

    const language = languageMap[langStr] || languageMap['Bash']

    hljs.registerLanguage(language.key, language.file)
    if (language.extraKeywords) {
      hljs.getLanguage(language.key).keywords += ` ${language.extraKeywords.join(' ')}`
    }

    return hljs.highlight(body.body, { language: language.key }).value
  }, [body.body, langStr])

  return (
    <div tw="mb-4 relative w-full" css={[isPrintView && tw`break-inside-avoid`]}>
      <pre tw="flex flex-col text-white leading-[2] bg-gray-800 rounded-2xl overflow-hidden" className="group">
        <div tw="flex-[1 0 auto] bg-gray-700 border-b-[1px] border-[#545E66] px-4 pt-2 pb-1 flex justify-between items-center">
          <span tw="text-xs italic text-tealBlue">{langStr}</span>
          {!isPrintView && <CopyButton text={body.body} />}
        </div>
        <Global
          styles={css`
            .hljs-built_in,
            .hljs-literal,
            .hljs-symbol,
            .hljs-attribute,
            .hljs-attr,
            .hljs-code,
            .hljs-regexp,
            .hljs-link {
              ${tw`text-amberOrange`}
            }
            .hljs-string,
            .hljs-meta-string,
            .hljs-selector-tag,
            .hljs-selector-id,
            .hljs-selector-class,
            .hljs-template-tag,
            .hljs-template-variable,
            .hljs-tag {
              ${tw`text-green-400`}
            }
            .hljs-keyword,
            .hljs-name,
            .hljs-quote,
            .hljs-type {
              ${tw`text-mediumPurple`}
            }
            .hljs-title {
              ${tw`text-orange-500`}
            }
            .hljs-params {
              ${tw`text-primaryBlue`}
            }
            .hljs-function {
              ${tw`text-melonRed`}
            }
            .hljs-comment {
              ${tw`text-gray-600 italic`}
            }
            .hljs-meta,
            .hljs-number {
              ${tw`text-gray-400`}
            }
          `}
        />
        <div
          className="bw-code-container"
          tw="flex-[1 0 auto] w-full h-full overflow-auto"
          css={css`
            ::-webkit-scrollbar {
              background: transparent;
              width: 14px;
              height: 14px;
            }
            ::-webkit-scrollbar-thumb {
              background-color: rgba(255, 255, 255, 0.3);
              border-radius: 0px;
            }
            ::-webkit-scrollbar-track {
              background: rgba(255, 255, 255, 0.1);
            }
            ::-webkit-scrollbar-corner {
              background: rgba(255, 255, 255, 0.1);
            }
            ::-webkit-scrollbar-button:start:decrement,
            ::-webkit-scrollbar-button:end:increment {
              background-color: rgba(255, 255, 255, 0.1);
              color: rgba(255, 255, 255, 0.3);
              width: 15px;
              height: 15px;
              display: block;
              background-size: 20px;
              background-repeat: no-repeat;
              background-position: center;
            }

            ::-webkit-scrollbar-button:vertical:decrement {
              background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="rgba(255, 255, 255, 0.5)" viewBox="0 0 20 20"><path d="M10 6l-4 4h8z"/></svg>');
            }
            ::-webkit-scrollbar-button:vertical:increment {
              background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="rgba(255, 255, 255, 0.5)" viewBox="0 0 20 20"><path d="M10 14l4-4h-8z"/></svg>');
            }
            ::-webkit-scrollbar-button:horizontal:decrement {
              background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="rgba(255, 255, 255, 0.5)" viewBox="0 0 20 20"><path d="M6 10l4-4v8z"/></svg>');
              width: 28px;
              background-position: 10px -4px;
            }
            ::-webkit-scrollbar-button:horizontal:increment {
              background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="rgba(255, 255, 255, 0.5)" viewBox="0 0 20 20"><path d="M14 10l-4 4v-8z"/></svg>');
            }
            ::-webkit-scrollbar-button:horizontal:increment:not(:corner-present) {
              width: 28px;
              background-position: -2px center;
            }
            ::-webkit-scrollbar-button:vertical:increment:not(:corner-present) {
              height: 28px;
            }
          `}
        >
          <div tw="flex pb-2 box-content">
            {/* eslint-disable-next-line react/no-danger */}
            <code
              tw="pl-4 pr-8 pt-4 pb-2 block "
              css={[wordWrap && tw`text-wrap break-normal`, isPrintView && tw`text-pretty break-all`]}
              dangerouslySetInnerHTML={{ __html: highlightedCode }}
            />
          </div>
        </div>
      </pre>
    </div>
  )
}
